import AbstractWidget, { lengthWithUnit } from './AbstractWidget';
import { TextWidget } from './TextWidget';
import { isNullOrEmpty, isNullOrWhitespace } from '../utils/object';
import { setSafeHtml } from '../utils/dom';

export default class AbstractBoxedWidget extends AbstractWidget {
    renderBox = (
        data: any,
        options: { [id: string]: any } = {
            element: 'div',
            showTitle: true,
            titleStyle: '',
            messagePadType: 'margin',
            allowBackgroundFill: true,
            settings: {}
        }
    ): HTMLDivElement | null => {
        if (this.container !== undefined && this.container !== null) {
            let doc = this.container.ownerDocument,
                txt = doc.createTextNode(
                    `⚠️ Override this to render content for the widget [${this.design.scriptClass}] ⚠️`
                ),
                span = doc.createElement('span'),
                settings = {
                    ...AbstractBoxedWidget.getDefaults(),
                    ...this.design,
                    ...(options.settings !== undefined ? options.settings : {}) // Allow override if sub-classes have different defaults
                },
                {
                    width,
                    height,
                    borderWidth,
                    borderRadius,
                    borderStyle,
                    borderColor,
                    controlBackground,
                    titleText,
                    boxTitleCssClass,
                    titleColor,
                    titleBackground,
                    titleIsBold,
                    titleAlignment,
                    titlePadding,
                    titleFontSize,
                    messageBackground,
                    messagePadding,
                    numberFormat,
                    locale,
                    noDataText,
                    description
                } = settings,
                bordered =
                    !isNullOrWhitespace(borderStyle) &&
                    borderStyle.toLowerCase().replace(/[^a-zA-Z]/g, '') !== 'notset';
            span.setAttribute('class', 'rb--generated');
            span.appendChild(txt);
            let box: HTMLDivElement = doc.createElement('div'),
                style = '',
                div,
                label;
            box.setAttribute(
                'class',
                `${options.boxClass !== undefined ? options.boxClass : 'ia-text-box'} rb--generated`.trim()
            );
            if (!isNullOrWhitespace(width)) {
                style += 'left: 0; right: 0;'; //`width: ${settings.width};`;
            }
            if (!isNullOrWhitespace(height)) {
                style += 'top: 0; bottom: 0;'; //`height: ${settings.height};`;
            }
            if (bordered) {
                if (!isNullOrWhitespace(borderWidth)) style += `border-width: ${lengthWithUnit(borderWidth)};`;
                if (!isNullOrWhitespace(borderRadius)) style += `border-radius: ${lengthWithUnit(borderRadius)};`;
                style += `border-style: ${borderStyle.toLowerCase()};`;
            }
            if (!isNullOrWhitespace(borderColor)) {
                // Force a background that matches border to avoid CSS oddness
                if (bordered && !options.allowBackgroundFill) {
                    style += `border-color: ${borderColor};`;
                } else if (bordered) {
                    style += `border-color: ${borderColor}; background-color: ${borderColor};`;
                }
            }
            //if (!isNullOrWhitespace(messageBackground)) style += `background-color: ${borderColor};`;
            if (!isNullOrWhitespace(controlBackground)) {
                style += `background: ${controlBackground};`;
            }
            if (style.length > 0) {
                box.setAttribute('style', style);
            }
            if (!isNullOrWhitespace(titleText) && options.showTitle) {
                div = doc.createElement('div');
                div.setAttribute('class', `ia-text-box-title ${boxTitleCssClass ?? ''}`.trim());
                style = '';
                if (!isNullOrWhitespace(titleColor)) {
                    style += `color: ${titleColor};`;
                }
                if (!isNullOrWhitespace(titleBackground)) {
                    style += `background: ${titleBackground};`;
                }
                if (titleIsBold) {
                    style += 'font-weight: bold;';
                }
                if (!isNullOrWhitespace(titleFontSize) && Number.parseInt(titleFontSize) > 0) {
                    style += `font-size: ${lengthWithUnit(titleFontSize)};`;
                }
                if (titleAlignment !== 'auto' && titleAlignment !== '') {
                    style += `text-align: ${titleAlignment.toLowerCase()};`;
                }
                if (!isNullOrWhitespace(borderRadius)) {
                    style += `border-top-left-radius: ${lengthWithUnit(
                        borderRadius
                    )}; border-top-right-radius: ${lengthWithUnit(borderRadius)};`;
                }
                if (options.titleStyle !== undefined) {
                    style += options.titleStyle; // Hard override
                }
                if (style.length > 0) {
                    div.setAttribute('style', style);
                }
                label = doc.createElement('div');
                label.setAttribute('id', `w${settings.id}_titleContent`);
                style = '';
                if (!isNullOrWhitespace(titlePadding)) {
                    style += `padding: ${lengthWithUnit(titlePadding, 'px', true, 4, true)};`;
                }
                if (style.length > 0) {
                    label.setAttribute('style', style);
                }
                let htmlTitleFragment = TextWidget.insertValuesIntoText(
                    titleText,
                    data,
                    numberFormat,
                    locale,
                    noDataText
                );
                if (htmlTitleFragment !== null && htmlTitleFragment.trim().indexOf('</') > 0) {
                    setSafeHtml(label, htmlTitleFragment.replace('<>', '').replace('</>', ''));
                }
                // Allow empty enclosers, React-style
                else {
                    label.appendChild(doc.createTextNode(htmlTitleFragment !== null ? htmlTitleFragment : ''));
                }
                div.appendChild(label);
                box.appendChild(div);
            }
            div = doc.createElement(options.element || 'div');
            div.setAttribute('class', `${options.messageContainerClass ?? 'ia-icon-container'} rb--generated`);
            style = '';
            if (messagePadding !== undefined && messagePadding !== null) {
                style += `${options.messagePadType || 'margin'}: ${lengthWithUnit(messagePadding, 'px', true, 4)};`; // Margin - reverse padding - makes layout of children easier
            }
            if (!isNullOrWhitespace(messageBackground)) {
                style += `background-color: ${messageBackground};`;
            }
            if (style.length > 0) {
                div.setAttribute('style', style);
            }
            box.appendChild(div);
            if (description !== undefined && description !== null && !isNullOrEmpty(description)) {
                const dd = doc.createElement('div');
                dd.setAttribute('class', `rb-widget-description rb--generated`);
                const htmlFragment = TextWidget.insertValuesIntoText(
                    description,
                    data,
                    numberFormat,
                    locale,
                    noDataText
                );
                if (htmlFragment !== null && htmlFragment.indexOf('</') > 0) {
                    setSafeHtml(dd, htmlFragment.replace('<>', '').replace('</>', ''));
                }
                // Allow empty enclosers, React-style
                else {
                    dd.appendChild(doc.createTextNode(htmlFragment !== null ? htmlFragment : ''));
                }
                if (!isNullOrWhitespace(messageBackground)) {
                    dd.style.backgroundColor = messageBackground;
                }
                box.appendChild(dd);
            }
            this.container.appendChild(box);
            return div;
        }
        return null;
    };

    static getDefaults = (): { [id: string]: any } => {
        return {
            ...AbstractWidget.getDefaults(),
            borderRadius: '2px',
            borderColor: '',
            borderStyle: 'none',
            borderWidth: '2px',
            messageBackground: 'transparent',
            messagePadding: '10px 10px 10px 10px',
            titleAlignment: 'auto',
            titleIsBold: true,
            titlePadding: '5px',
            titleText: '',
            titleBackground: '',
            titleColor: ''
        };
    };
}
